import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import type React from "react";
import { Button, Grid, Paper } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClassParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { ConfirmationDialogRaw } from "../../../../../controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { useDeleteDelayMutation } from "../../../hooks/mutations";
import { useTableData } from "../../hooks/hooks";
import { useDelaysOptionFunctions } from "../../hooks/useDelaysOptionFunctions";
import { DelayByShift } from "../../models/DelayByShift";
import { SplitDelays } from "../modals/SplitDelays";
import { UpsertJustifyDelay } from "../modals/UpsertJustifyDelay";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

type Props = {
  isLoading: boolean;
  delays: DelayByShift[];
  assetId?: number;
  delayId?: number;
};

export const DelaysManagerTable = ({
  isLoading: isLoadingGrid,
  delays,
  assetId,
  delayId,
}: Props) => {
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<
    "new" | "edit" | "delete" | "justify" | "split" | ""
  >("");

  const deleteMutation = useDeleteDelayMutation();

  const getRowStyle = (params: RowClassParams) => {
    if (delayId && params.data.DelayId === delayId) {
      return { background: "lightblue" };
    }
    if (!params.data.DelayCodeName || !params.data.DelayCategoryName) {
      return { background: "#E8C8C8" };
    }
    if (params.data.IsMicroDelay) {
      return { background: "#E8EBEB" };
    }
  };

  const getRowClass = (params: RowClassParams) => {
    if (delayId && params.data.DelayId === delayId) {
      return "";
    }
    if (!params.data.DelayCodeName || !params.data.DelayCategoryName) {
      return "unclassified-row";
    }
    return "";
  };

  const {
    createData,
    deleteData,
    justifyData,
    getMenuOptions,
    messageDeleteModal,
    setMessageDeleteModal,
  } = useDelaysOptionFunctions({ setSelectedId, setShowModal });

  const { showContextMenu, registerConfig } = useContextMenuMESF();
  const { columnDefs } = useTableData({
    justifyData: justifyData,
    deleteData: deleteData,
    showContextMenu: showContextMenu,
  });

  const handleDeleteDelay = (value?: string) => {
    setShowModal("");
    setMessageDeleteModal("");
    if (value !== "OK") return;
    if (selectedId === null) return;
    deleteMutation.mutate(selectedId, {
      onSuccess: () => {
        setNotificationMessage("Delay deleted successfully");
        setSelectedId(null);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : "Failed to delete delay");
      },
    });
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;
    // ag-grid internally passes the DOM event but it's not in the public types
    const nativeEvent = (
      params as GetContextMenuItemsParams & { event?: MouseEvent }
    ).event;
    if (nativeEvent) {
      showContextMenu(
        nativeEvent as unknown as React.MouseEvent,
        data,
        "delayManagerContext",
      );
    }
    if (data) {
      params.api.deselectAll();
      params.node?.setSelected(true);
    }
    return [];
  };

  useEffect(() => {
    registerConfig({
      id: "delayManagerContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <div onContextMenu={(e) => e.preventDefault()}>
        <Grid container>
          <Grid
            component={Paper}
            elevation={3}
            item
            md={12}
            xs={12}
            style={{
              padding: 10,
              height: "calc(100vh - 300px)",
              minHeight: "calc(100vh - 300px)",
            }}
          >
            <AgGridReact<DelayByShift>
              rowData={delays}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getContextMenuItems={getContextMenuItems}
              onCellDoubleClicked={(e) => justifyData(e.data!)}
              getRowId={(params) => String(params.data.DelayId)}
              loading={isLoadingGrid}
              getRowStyle={getRowStyle}
              getRowClass={getRowClass}
              {...defaultGridOptions}
              suppressContextMenu={false}
            />
            <Grid container justifyContent="flex-end">
              <Grid
                item
                md={2}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  style={{ margin: "1.5rem 0px 0px" }}
                  onClick={() => {
                    createData();
                  }}
                >
                  NEW DELAY
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <ConfirmationDialogRaw
        title="Confirmation"
        message={messageDeleteModal}
        open={showModal === "delete"}
        onClose={handleDeleteDelay}
      />
      <UpsertJustifyDelay
        showModal={showModal}
        onHide={() => {
          setShowModal("");
          setSelectedId(null);
        }}
        delayId={selectedId}
        assetId={assetId}
      />
      <SplitDelays
        show={showModal === "split"}
        onHide={() => {
          setShowModal("");
          setSelectedId(null);
        }}
        delayId={selectedId}
        assetId={assetId ?? null}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
